<template>
  <footer ref="footerRef" class="subscription-container">
    <button
      class="subscription-button"
      type="button"
      aria-label="Скачать в Google Play"
      @click="handleClick"
    >
      Скачать в Google Play
    </button>
  </footer>
</template>

<script setup>
import { onMounted, onBeforeUnmount, ref } from 'vue'

defineProps({
  handleClick: {
    type: Function,
    required: true,
  },
})

const footerRef = ref(null)
let lastTransform = ''
let lastBottom = ''

function updateFooterPosition() {
  const footer = footerRef.value
  if (!footer) return

  const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight
  const windowInnerHeight = window.innerHeight

  const footerRect = footer.getBoundingClientRect()
  const overflow = footerRect.bottom - viewportHeight
  const heightDiff = windowInnerHeight - viewportHeight

  let newTransform = 'translateY(0)'
  let newBottom = '0'

  if (overflow > 0) {
    newTransform = `translateY(-${overflow}px)`
    newBottom = '0'
  } else if (heightDiff > 150) {
    newTransform = 'translateY(0)'
    newBottom = `${heightDiff}px`
  }

  if (lastTransform !== newTransform) {
    footer.style.transform = newTransform
    lastTransform = newTransform
  }
  if (lastBottom !== newBottom) {
    footer.style.bottom = newBottom
    lastBottom = newBottom
  }
}

let throttleTimeout = null
function throttledUpdate() {
  if (throttleTimeout) return
  throttleTimeout = setTimeout(() => {
    updateFooterPosition()
    throttleTimeout = null
  }, 0)
}

onMounted(() => {
  updateFooterPosition()

  const target = window.visualViewport || window
  target.addEventListener('resize', throttledUpdate)
  target.addEventListener('scroll', throttledUpdate)

  window.addEventListener('focus', throttledUpdate)
  window.addEventListener('blur', throttledUpdate)
})

onBeforeUnmount(() => {
  const target = window.visualViewport || window
  target.removeEventListener('resize', throttledUpdate)
  target.removeEventListener('scroll', throttledUpdate)

  window.removeEventListener('focus', throttledUpdate)
  window.removeEventListener('blur', throttledUpdate)

  if (throttleTimeout) {
    clearTimeout(throttleTimeout)
  }
})
</script>

<style scoped>
footer.subscription-container {
  position: fixed;
  touch-action: none;
  user-select: none;
  left: 0;
  bottom: 0;
  width: 100%;
  background: #fff;
  box-shadow: 0 -1px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 10;
  transition: transform 0.3s ease, bottom 0.3s ease, opacity 0.2s ease;
}

.subscription-button {
  font-family: Play, sans-serif;
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 400px;
  height: 60px;
  background: #3b82f4;
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 22px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.subscription-button:hover {
  transform: scale(1.02);
}

.subscription-button::before {
  content: '';
  position: absolute;
  top: -20%;
  left: -50%;
  width: 40%;
  height: 140%;
  background: rgba(255, 255, 255, 0.4);
  transform: skewX(-25deg);
  filter: blur(8px);
  animation: slideShine 3s ease-in-out infinite;
  pointer-events: none;
}

@keyframes slideShine {
  0% { left: -50% }
  50% { left: 120% }
  100% { left: 120% }
}

.subscription-button::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.2) 0%, transparent 70%);
  border-radius: 12px;
  transform: translate(-50%, -50%) scale(0);
  animation: pulseAura 4s ease-in-out infinite;
  pointer-events: none;
}

@keyframes pulseAura {
  0%, 70% {
    transform: translate(-50%, -50%) scale(0);
    opacity: 0;
  }
  80% {
    transform: translate(-50%, -50%) scale(1.1);
    opacity: 0.2;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.3);
    opacity: 0;
  }
}
</style>
